import type { CompletedRunInput } from "../scoring/completedRun";
import type { LeaderboardEntry } from "./core";

export interface SubmitRunResult {
  readonly outcome: "saved" | "duplicate";
  /** Null when the run did not make the fetched top of the board. */
  readonly submittedRank: number | null;
  readonly submittedEntry?: {
    readonly playerName?: string | null;
    readonly name?: string | null;
  };
}

export interface LeaderboardGateway {
  submitRun(input: CompletedRunInput): Promise<SubmitRunResult>;
  updatePlayerName(runId: string, playerName: string): Promise<unknown>;
  listEntries(): Promise<readonly LeaderboardEntry[]>;
}

export interface LeaderboardViewState {
  readonly status: "idle" | "submitting" | "saved" | "unavailable";
  readonly runId: string | null;
  readonly rank: number | null;
  readonly playerName: string | null;
  /** Top of the board, loaded separately from the submission. */
  readonly entries: readonly LeaderboardEntry[];
  readonly boardStatus: "idle" | "loading" | "ready" | "unavailable";
}

const IDLE_STATE: LeaderboardViewState = Object.freeze({
  status: "idle",
  runId: null,
  rank: null,
  playerName: null,
  entries: Object.freeze([]),
  boardStatus: "idle",
});

export class LeaderboardAdapter {
  private currentState = IDLE_STATE;
  private readonly savedRunIds = new Set<string>();
  private readonly submissions = new Map<
    string,
    Promise<LeaderboardViewState>
  >();

  constructor(private readonly gateway: LeaderboardGateway) {}

  get state(): LeaderboardViewState {
    return this.currentState;
  }

  /**
   * Drop the finished run's view state so the next one starts blank.
   *
   * `savedRunIds` and `submissions` deliberately survive: they are the dedupe
   * and in-flight guards, and clearing them would let a run be posted twice.
   */
  reset(): void {
    this.currentState = IDLE_STATE;
  }

  submitCompletedRun(input: CompletedRunInput): Promise<LeaderboardViewState> {
    if (this.savedRunIds.has(input.runId)) {
      return Promise.resolve(this.currentState);
    }
    const existing = this.submissions.get(input.runId);
    if (existing) {
      return existing;
    }

    this.currentState = Object.freeze({
      ...this.currentState,
      status: "submitting",
      runId: input.runId,
      rank: null,
      playerName: null,
    });
    // A submission that resolves after a newer run has started must not
    // overwrite the newer run's state — it would show the old rank, and leave
    // `currentState.runId` pointing at the row `updatePlayerName` would patch.
    const publish = (state: LeaderboardViewState): LeaderboardViewState => {
      if (this.currentState.runId === input.runId) {
        this.currentState = state;
      }
      return state;
    };

    const submission = this.gateway
      .submitRun(input)
      .then((result) => {
        this.savedRunIds.add(input.runId);
        const entry = result.submittedEntry;
        return publish(
          Object.freeze({
            ...this.currentState,
            status: "saved",
            runId: input.runId,
            rank: result.submittedRank,
            playerName: entry?.playerName ?? entry?.name ?? null,
          }),
        );
      })
      .catch((error: unknown) => {
        console.warn("Leaderboard submission failed", error);
        return publish(
          Object.freeze({
            ...this.currentState,
            status: "unavailable",
            runId: input.runId,
            rank: null,
            playerName: null,
          }),
        );
      })
      .finally(() => {
        this.submissions.delete(input.runId);
      });
    this.submissions.set(input.runId, submission);
    return submission;
  }

  async updatePlayerName(playerName: string): Promise<LeaderboardViewState> {
    const runId = this.currentState.runId;
    if (!runId || !this.savedRunIds.has(runId)) {
      return this.currentState;
    }
    await this.gateway.updatePlayerName(runId, playerName);
    this.currentState = Object.freeze({
      ...this.currentState,
      playerName,
    });
    // The fetched page still carries the old name; the ranked columns do not
    // include run_id, so there is no row to patch locally.
    if (this.currentState.boardStatus === "ready") {
      return this.loadBoard();
    }
    return this.currentState;
  }

  /**
   * Fetch the top of the board.
   *
   * Kept separate from the submission so a board outage never makes a saved
   * run look unsaved, and vice versa.
   */
  async loadBoard(): Promise<LeaderboardViewState> {
    this.currentState = Object.freeze({
      ...this.currentState,
      boardStatus: "loading",
    });
    try {
      const entries = await this.gateway.listEntries();
      this.currentState = Object.freeze({
        ...this.currentState,
        entries: Object.freeze([...entries]),
        boardStatus: "ready",
      });
    } catch (error) {
      console.warn("Leaderboard fetch failed", error);
      this.currentState = Object.freeze({
        ...this.currentState,
        entries: Object.freeze([]),
        boardStatus: "unavailable",
      });
    }
    return this.currentState;
  }
}
